import { addDays, addHours, addMinutes, addMonths, addQuarters, addSeconds, addWeeks, addYears, } from 'date-fns';
import { DateTime } from '../../DateTime';
import { ExprError } from '../ExprError';
import { getTimeUnit } from '../ExprTime';
import * as Types from '../../Types';
import { validateArguments } from '../../SignatureValidation';
export var DateAddFn;
(function (DateAddFn) {
    DateAddFn.Name = `DATEADD`;
    DateAddFn.Stage = "SCALAR";
    DateAddFn.Signature = `DATEADD(<unit>, <interval>, <datetime>)`;
    function make(callName, args, type, role) {
        return {
            name: DateAddFn.Name,
            stage: DateAddFn.Stage,
            data: { type, role },
            value: (...argVals) => {
                const [datePart, interval, date] = argVals;
                const [, , dateInfo] = args;
                if (typeof datePart !== 'string') {
                    throw new Error(`Invalid date part input for DATEADD: ${datePart}`);
                }
                if (interval == null || typeof interval !== 'number') {
                    throw new Error(`Invalid interval input for DATEADD: ${interval}`);
                }
                if (date == null || typeof date !== 'number') {
                    throw new Error(`Invalid date input for DATEADD: ${date}`);
                }
                let dateMillis = 0;
                switch (getTimeUnit(dateInfo)) {
                    case "MILLIS":
                        dateMillis = date;
                        break;
                    case "MICROS":
                        dateMillis = date / 1000;
                        break;
                    case "SECOND":
                        dateMillis = date * 1000;
                        break;
                }
                let totalMillis;
                switch (DateTime.unit(datePart)) {
                    case "YEAR":
                        totalMillis = addYears(dateMillis, interval).valueOf();
                        break;
                    case "QUARTER":
                        totalMillis = addQuarters(dateMillis, interval).valueOf();
                        break;
                    case "MONTH":
                        totalMillis = addMonths(dateMillis, interval).valueOf();
                        break;
                    case "WEEKDAY":
                    case "DAYOFYEAR":
                    case "DAY":
                        totalMillis = addDays(dateMillis, interval).valueOf();
                        break;
                    case "WEEK":
                        totalMillis = addWeeks(dateMillis, interval).valueOf();
                        break;
                    case "HOUR":
                        totalMillis = addHours(dateMillis, interval).valueOf();
                        break;
                    case "MINUTE":
                        totalMillis = addMinutes(dateMillis, interval).valueOf();
                        break;
                    case "SECOND":
                        totalMillis = addSeconds(dateMillis, interval).valueOf();
                        break;
                    default:
                        throw new Error(`Time unit not supported: ${datePart}`);
                }
                if (type.name === "TIMESTAMP") {
                    switch (type.unit) {
                        case "MILLIS":
                            return totalMillis;
                        case "MICROS":
                            return totalMillis * 1000;
                        case "SECOND":
                            return totalMillis / 1000;
                        default:
                            throw new Error(`Unsupported time unit`);
                    }
                }
                else {
                    throw new Error(`Data type not supported in DATEADD function: ${type.name}`);
                }
            },
        };
    }
    DateAddFn.make = make;
    function validate(callNode, callName, args) {
        const sig = {
            meta: { name: DateAddFn.Name, signature: DateAddFn.Signature, location: callNode.location },
            arguments: [
                {
                    type: "STRING",
                    customValidation: (unit) => {
                        const errors = [];
                        if (!(Types.isStringLiteral(unit) && DateTime.unit(unit.value))) {
                            const validUnits = DateTime.validUnits.join(`', '`);
                            const displayValue = Types.isLiteralLike(unit)
                                ? unit.value
                                : Types.getResolvedType(unit);
                            errors.push(new ExprError.InvalidArgument(DateAddFn.Name, DateAddFn.Signature, `expects <unit> to be one of '${validUnits}', saw: ` +
                                `<${displayValue}>`, callNode.location));
                        }
                        return errors;
                    },
                },
                "NUMERIC",
                {
                    customValidation: (arg) => {
                        switch (Types.getResolvedType(arg)) {
                            case "DATE_TIME":
                            case "TIMESTAMP":
                                return [];
                        }
                        return [
                            new ExprError.InvalidArgument(DateAddFn.Name, DateAddFn.Signature, `expected <datetime> to be an instance of either DATETIME or TIMESTAMP but received ${Types.getResolvedType(arg)}`, callNode.location),
                        ];
                    },
                },
            ],
        };
        return validateArguments(sig, args);
    }
    DateAddFn.validate = validate;
})(DateAddFn || (DateAddFn = {}));
